<template>
  <div>
    <div v-show="isAccesstable" class="group-detail">
      <h1 class="common__main-title">Class Record</h1>
      <div class="sub-title mb-2">
        <div v-if="lesson" class="row w-100">
          <div class="col-2">
            <router-link v-if="!isConfirm && lesson.prevUnit"
              :to="{
                name: 'ClassRecordEdit',
                params: { lessonId: lesson.prevUnit.lessonId },
              }">
              &lt; Unit {{ lesson.prevUnit.unit }}
            </router-link>
          </div>
          <div class="col-8">
            <span class="lesson-info">
              【Unit {{ lesson.unit }}】
              {{ lesson.date.format('YYYY/MM/DD')}} {{ lesson.date | weekday }}
              Period{{ lesson.period }}
              ({{ lesson.campus.name }} {{ lesson.booth | booth }})
            </span><br>
            <span class="course-name">{{ lesson.course.name }}</span>
          </div>
          <div class="col-2">
            <router-link v-if="!isConfirm && lesson.nextUnit"
              :to="{
                name: 'ClassRecordEdit',
                params: { lessonId: lesson.nextUnit.lessonId },
              }">
              Unit {{ lesson.nextUnit.unit }} &gt;
            </router-link>
          </div>
        </div>
      </div>
      <div class="preparation-data mb-2">
        <template v-if="lesson && (lesson.unit === 5 || lesson.unit === 10)">
          <a href="/resources/CEFR.pdf" target="_blank" rel="noopener noreferrer">CEFR</a>
          <span class="mx-2">/</span>
        </template>
        <router-link
          v-if="lesson && lesson.fiscalYear <= 2023"
          :to="{ name: 'OldLMSData' }">
          Preparation data
        </router-link>
        <router-link
          v-else
          :to="{ name: 'Preparation' }">
          Preparation data
        </router-link>
      </div>
      <div class="student-list mb-2">
        <button v-for="(student, idx) in students" :key="idx"
          :class="{ 'student-button': true, 'active': selectedStudentId === student.id }"
          @click="selectedStudentId = student.id">
          {{ student.name }}
        </button>
        <button
          :class="{ 'student-button': true, 'active': selectedStudentId === null }"
          @click="selectedStudentId = null">
          Comment (Common)
        </button>
      </div>

      <ValidationObserver ref="observer">
        <StudentRecord class="mb-5"
          v-for="(studentWithGrade, i) in studentsWithGrade" :key="i"
          v-show="selectedStudentId === studentsWithGrade[i].student.id"
          :isConfirm="isConfirm || studentWithGrade.isLocked"
          v-model="studentsWithGrade[i]"
        />
        <CommonRecord class="mb-5"
          v-show="selectedStudentId === null"
          :isConfirm="
            isConfirm
          "
          v-model="comment"
        />
      </ValidationObserver>

      <div v-if="lesson" class="d-flex justify-content-between">
        <template v-if="isConfirm">
          <b-button pill variant="secondary" @click="switchConfirm(false)">
            Back
          </b-button>
          <b-button pill variant="primary" @click="register">
            Send
          </b-button>
        </template>
        <template v-else>
          <b-button pill variant="secondary" :to="{ name:'Home' }">Back</b-button>

          <template v-if="
            studentsWithGrade.filter(s => s.isLocked).length === studentsWithGrade.length
          ">
            <b-button v-if="isAcceptableInquiry" pill variant="primary" @click="acceptInquiry">
              Accept Evaluation
            </b-button>
            <b-button
            pill variant="primary"
            :to="{ name: 'ApplicationAdd' }"
            :disabled="!isEvaluable"
            >
              Apply inquiry
            </b-button>
          </template>
          <template v-else>
            <template v-if="isEvaluable">
              <b-button pill variant="primary" @click="temporarilySave">
                Temporarily saved
              </b-button>
              <b-button pill variant="primary" @click="switchConfirm(true)">
                Confirm
              </b-button>
            </template>
          </template>
        </template>
      </div>
    </div>
    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import _ from 'lodash'
import moment, { Moment } from 'moment'
import { ValidationObserver } from 'vee-validate'
import {
  ClassRecord,
  ClassRecordForRegister,
  Criterion,
  Rating,
  StudentWithGrade,
} from '@/store/lessons/classRecord/types'
import { RootState } from '@/store/types'
import Loading from '@/components/parts/modal/Loading.vue'
import StudentRecord from '@/components/parts/lessons/classRecord/StudentRecord.vue'
import CommonRecord from '@/components/parts/lessons/classRecord/CommonRecord.vue'
import { EvaluationInquiryForAccept } from '@/store/applications/evaluationInquiries/types'
import { Booth } from '@/store/booths/types'

export default Vue.extend({
  components: {
    ValidationObserver,
    StudentRecord,
    CommonRecord,
    Loading,
  },
  filters: {
    weekday: (date: Moment) => date.format('ddd'),
    booth: (b: Booth): string => `${b.building}-${b.room}-${b.number}`,
  },

  data: (): {
    isLoading: boolean,
    isConfirm: boolean,
    selectedStudentId: string|null,
    studentsWithGrade: StudentWithGrade[],
    comment: string,
  } => ({
    isLoading: false,
    isConfirm: false,
    selectedStudentId: null,
    studentsWithGrade: [],
    comment: '',
  }),
  computed: {
    lesson(): ClassRecord {
      return this.$store.getters.findClassRecordByLessonId(this.$route.params.lessonId)
    },
    students(): { id: string, name: string }[] {
      return this.lesson?.students.map((s) => s.student) || []
    },
    /**
     * アクセス権限チェック
     * @returns {boolean} ture: アクセスOK, false: アクセスNG
     */
    isAccesstable(): boolean {
      const { profile } = (this.$store.state as RootState).auth
      if (_.indexOf(['admin', 'senior'], profile?.authority) !== -1) {
        // 管理者、シニアはOK
        return true
      }

      // レッスンの教師か代講チューターであればOK
      return this.lesson?.teacher?.id === profile?.id
        || this.lesson?.substituteTutor?.id === profile?.id
    },

    /**
     * 評価可能かどうか判定
     * @return {boolean} true: 評価可能, false: 不可
     */
    isEvaluable(): boolean {
      // 未来のレッスンは不可
      if (moment().format('YYYYMMDD') < this.lesson.date.clone().format('YYYYMMDD')) {
        return false
      }
      // 評価承認待ちの場合は不可
      if (_.filter(this.studentsWithGrade, { isWaitingApproved: true }).length > 0
          || _.filter(this.studentsWithGrade, { isApplication: true }).length > 0
      ) {
        return false
      }

      // レッスンの教師以外は不可
      const { profile } = (this.$store.state as RootState).auth
      if (
        this.lesson?.teacher?.id === profile?.id
        || this.lesson?.substituteTutor?.id === profile?.id
      ) {
        return true
      }

      // 権限がseniorの場合は評価・申請可
      if (profile?.authority === 'senior') {
        return true
      }

      return false
    },

    /**
     * 評価申請後の修正承認可否
     * @return {boolean} true: 修正承認可能, false: 不可
     */
    isAcceptableInquiry(): boolean {
      const lockedGradesCount = _.filter(this.studentsWithGrade, { isLocked: true }).length
      if (this.studentsWithGrade.length > lockedGradesCount) {
        // 全てロック状態出ない場合false
        return false
      }

      if (_.filter(this.studentsWithGrade, { isWaitingApproved: true }).length === 0) {
        // 評価承認待ちが存在しない場合はfalse
        return false
      }

      const { profile } = (this.$store.state as RootState).auth

      if (_.filter(this.studentsWithGrade, { applicantId: profile?.id }).length > 0) {
        // 申請者の場合はfalse
        return false
      }

      if (profile?.authority === 'senior') {
        // 権限がseniorの場合のみtrue
        return true
      }

      return false
    },
  },

  created() {
    this.fetch()
  },
  watch: {
    // ルートが変更されたらこのメソッドを再び呼び出します
    $route: 'fetch',
  },
  methods: {
    /**
     * データ取得
     */
    fetch() {
      this.isLoading = true
      this.$store.dispatch('getClassRecordByLessonId', { lessonId: this.$route.params.lessonId })
        .finally(() => {
          // アクセス権限チェック
          if (!this.isAccesstable) {
            this.$router.push({ name: 'Home' })
            return
          }

          if (this.lesson.fiscalYear <= 2023) {
            this.$router.push({ name: 'OldLMSData' })
            return
          }

          this.isLoading = false
          // その他データ取得
          this.studentsWithGrade = _.chain(this.lesson)
            .get('students').map(_.cloneDeep)
            .value() || []
          this.comment = _.chain(this.studentsWithGrade)
            .find((s) => !s.isLocked) // ロックされていない(修正を申請した)履修生のコメントを取得
            .get('forEdit.comment')
            .value() || _.chain(this.studentsWithGrade)
            .first()
            .get('forEdit.comment')
            .value() || ''
          this.selectedStudentId = _.chain(this.studentsWithGrade).first().get('student.id').value() || null
        })
    },
    /**
     * 入力画面と確認画面の切り替え
     * @param {boolean} isConfirm true: 確認画面、 false: 入力画面
     */
    async switchConfirm(isConfirm: boolean) {
      if (this.lesson.fiscalYear <= 2023) {
        this.$bvModal.msgBoxOk('Error - This data from 2021 to 2023 cannot be registered.');
        return
      }
      if (await (this.$refs.observer as any).validate() !== true) {
        this.$bvModal.msgBoxOk('Please rate all students and write comment')
        return
      }

      if (isConfirm) {
        // 特別配慮が選択されているか判定
        const isSelectedSpecialConsideration = (() => {
          const criteria: Criterion[] = _.chain(this.studentsWithGrade)
            .first()
            .get('forEdit.criteria')
            .value()
          const specialConsiderationRatingId = _.chain(criteria)
            .find({ name: 'Attendance' })
            .get('ratings')
            .find((rating) => /.*Special consideration.*/.test(rating.description))
            .get('id')
            .value()

          return _.chain(this.studentsWithGrade)
            .filter((grade) => {
              return !grade.isLocked
            })
            .filter((grade) => {
              return grade.forEdit.criteria.filter((criterion) => {
                return criterion.selectedRatingId === specialConsiderationRatingId
              }).length > 0
            })
            .value().length > 0
        })()

        // チューターが「Special consideration」を選択している場合はエラー
        if (
          (this.$store.state as RootState).auth.profile?.authority === 'tutor'
          && isSelectedSpecialConsideration
        ) {
          this.$bvModal.msgBoxOk('Special consideration cannot be selected.')
          return
        }

        const isSelectedOver45min = (() => {
          const criteria: Criterion[] = _.chain(this.studentsWithGrade)
            .first()
            .get('forEdit.criteria')
            .value()
          const over45minRatingId = _.chain(criteria)
            .find({ name: 'Attendance' })
            .get('ratings')
            .find((rating) => /.*Over 45 min.*/.test(rating.description))
            .get('id')
            .value()

          return _.chain(this.studentsWithGrade)
            .filter((grade) => {
              return !grade.isLocked
            })
            .filter((grade) => {
              return grade.forEdit.criteria.filter((criterion) => {
                return criterion.selectedRatingId === over45minRatingId
              }).length > 0
            })
            .value().length > 0
        })()

        // 「Over 45 min」が選択されている場合はアラートを上げる
        if (
          isSelectedOver45min
          && (await this.$bvModal.msgBoxConfirm('Selected Over 45 min. Can i register?') !== true)
        ) {
          return
        }
      }

      this.selectedStudentId = _.chain(this.students).first().get('id').value() || null
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      this.isConfirm = isConfirm
    },

    /**
     * クラスレコードデータを一時保存する
     */
    async temporarilySave() {
      if (this.isLoading) {
        return
      }

      try {
        this.isLoading = true
        await Promise.all(this.studentsWithGrade
          .filter((s) => !s.isLocked)
          .map(({ student, forEdit }) => {
            const classRecord: ClassRecordForRegister = {
              studentId: student.id,
              lessonId: this.lesson.lessonId,
              criteria: forEdit.criteria
                .filter((c) => c.selectedRatingId)
                .map((c) => ({
                  id: c.id,
                  name: c.name,
                  selectedRating: _.find(c.ratings, { id: c.selectedRatingId }) as Rating,
                })),
              note: forEdit.note,
              comment: this.comment,
            }

            return this.$store.dispatch('temporarilySaveStudentClassRecord', classRecord)
          }))

        this.isLoading = false
        this.$bvModal.msgBoxOk('Success !')
          .then(() => {
            this.$router.go(0)
          })
      } catch (error: any) {
        this.isLoading = false
        if (_.has(error, 'response')) {
          this.$bvModal.msgBoxOk(`Failed: ${error.response.data.message}`)
        } else {
          this.$bvModal.msgBoxOk(`Failed: ${error.message}`)
        }
      }
    },

    /**
     * クラスレコードデータを登録する
     */
    async register() {
      if (this.isLoading) {
        return
      }

      try {
        this.isLoading = true
        // グループ全体の評価を一度に送信
        const studentGrades = this.studentsWithGrade.map(({ student, forEdit }) => ({
          studentId: student.id,
          criteria: forEdit.criteria.map((c) => ({
            id: c.id,
            name: c.name,
            selectedRatingId: c.selectedRatingId,
            point: _.find(c.ratings, { id: c.selectedRatingId })?.point || 0,
            description: _.find(c.ratings, { id: c.selectedRatingId })?.description || '',
          })),
          note: forEdit.note,
        }))

        await this.$store.dispatch('registerGroupClassRecord', {
          lessonId: this.lesson.lessonId,
          studentGrades,  // グループ内の履修生の評価データを配列で送信
          comment: this.comment,
        })

        this.isLoading = false
        this.$bvModal.msgBoxOk('Success !')
          .then(() => {
            this.$router.go(0)
          })
      } catch (error: any) {
        this.isLoading = false
        if (_.has(error, 'response')) {
          this.$bvModal.msgBoxOk(`Failed: ${error.response.data.message}`)
        } else {
          this.$bvModal.msgBoxOk(`Failed: ${error.message}`)
        }
      }
    },

    /**
     * 評価申請問い合わせの修正承認
     */
    async acceptInquiry() {
      if (!this.isAcceptableInquiry || this.isLoading) {
        return
      }

      if (await this.$bvModal.msgBoxConfirm('Are you sure accept evaluation from inquiry application?') !== true) {
        return
      }

      try {
        this.isLoading = true
        await Promise.all(this.studentsWithGrade
          .filter((s) => s.isWaitingApproved)
          .map((s) => {
            const params: EvaluationInquiryForAccept = {
              lessonId: this.$route.params.lessonId,
              studentId: s.student.id,
            }
            return this.$store.dispatch('acceptClassRecordEvaluationWithInquiry', params)
          }))

        this.isLoading = false
        this.$bvModal.msgBoxOk('Success !')
          .then(() => {
            this.$router.go(0)
          })
      } catch (error: any) {
        this.isLoading = false
        if (_.has(error, 'response')) {
          this.$bvModal.msgBoxOk(`Failed: ${error.response.data.message}`)
        } else {
          this.$bvModal.msgBoxOk(`Failed: ${error.message}`)
        }
      }
    },
  },
})
</script>

<style scoped>

.sub-title {
  padding:10px;
  text-align: center;
  background: #D8D8D8;
  display: flex;
  justify-content: space-between;
  font-size: 16px;
}

.sub-title a{
  font-size: 14px;
}

.preparation-data{
  text-align: right;
  font-size: 14px;
  margin-right: 35px;
}

.preparation-data a {
  font-size: 15px;
}

.student-list{
  display: flex;
  justify-content:space-between;
}

.student-button{
  padding: 15px;
  text-align: center;
  background-color: #E7E6E6;
  flex-basis:100%;
  margin: 0px 7px;
  border-style: none;
  cursor: pointer;
}

.active {
  background-color: #1cc8a0;
}

input[type="text"], select{
  padding: 5px;
  border: 1px solid #AAAFB6;
  border-radius: 30px;
  width:100%;
}

.lesson-info {
  font-size: 18px;
}

.course-name {
  font-size: 15px;
}
</style>
